import { CheckIcon, DownloadIcon, UploadIcon, XIcon } from "@primer/octicons-react";
import { Box, FormControl, Link, TextInput, Select } from '@primer/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

import { uploadApi } from '../../../api/uploadApi';
import { lichSuXuongPhongYTeApi } from '../../../api/lichSuXuongPhongYTeApi';
import { appInfo } from '../../../AppInfo';
import { AnimationPopup } from '../../../components/modal';
import { PopUpFormActions } from '../../../components/pop-up-form/PopUpForm';
import Steps, { IStepData } from '../../../components/steps/Steps';
import Button from '../../../components/ui/button';

import { NotifyHelper } from '../../../helpers/toast';
import { useCommonSelectedHook } from "../../../hooks/useCommonSelectedHook";

interface IImportLichSuYTeModalProps {
    onClose: () => void;
    animationOf: string;
}

const ImportLichSuYTeModal = (props: IImportLichSuYTeModalProps) => {
    const { onClose, animationOf } = props;

    const [stepId, setStepId] = useState(1);
    const [uploadedFileName, setUploadedFileName] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const componentDidMount = useRef<boolean>(false);

    const [dataSourceValidated, setDataSourceValidated] = useState<any[]>([]);
    const [filterStatus, setFilterStatus] = useState<string>('all'); // 'all', 'valid', 'error'
    const [importResult, setImportResult] = useState<any>(null);
    const { nam_hoc, dm_coso_id } = useCommonSelectedHook();
    const modalId = `import-modal-${Math.random().toString(36).substring(7)}`;

    const statistics = useMemo(() => {
        const total = dataSourceValidated.length;
        const errorCount = dataSourceValidated.filter((x) => x.err && x.err.trim() !== "").length;
        const validCount = total - errorCount;
        return { total, validCount, errorCount };
    }, [dataSourceValidated]);

    const filteredData = useMemo(() => {
        if (filterStatus === 'valid') {
            return dataSourceValidated.filter((x) => !x.err || x.err.trim() === "");
        } else if (filterStatus === 'error') {
            return dataSourceValidated.filter((x) => x.err && x.err.trim() !== "");
        }
        return dataSourceValidated;
    }, [dataSourceValidated, filterStatus]);

const _steps: IStepData[] = [
    {
        id: 1,
        name: "Upload file",
        is_active: true
    },
    {
        id: 2,
        name: "Validate",
        is_active: false
    },
    {
        id: 3,
        name: "Import",
        is_active: false
    },
    {
        id: 4,
        name: "Result",
        is_active: false
    }
];

    const handleSelectFile = () => {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute(
            'accept',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel'
        );
        input.click();
        input.onchange = async () => {
            var file = input && input.files ? input.files[0] : null;
            if (file) {
                setIsLoading(true);
                const res = await uploadApi.uploadExcel(file);
                if (res.is_success) {
                    setUploadedFileName(res.data.fileNameId);
                    setStepId(2);
                } else {
                    NotifyHelper.Error(res.message ?? "Error");
                }
                setIsLoading(false);
            }
        };
        input.remove();
    };

    useEffect(() => {
        componentDidMount.current = true;
        if (uploadedFileName !== "") {
            handleReadUploadedFile();
        }
        return () => {
            componentDidMount.current = false;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uploadedFileName]);



    const handleReadUploadedFile = async () => {
        setIsLoading(true);

        if (!componentDidMount.current) return;

        // Directly validate the data
        await handleValidateImportFile();
        setIsLoading(false);
    };

    const handleValidateImportFile = async () => {
      setIsLoading(true);
      const datasend = {
          fileName: uploadedFileName,
          sheetIndex: 0
      };
      const res = await lichSuXuongPhongYTeApi.validateImport(datasend, nam_hoc, dm_coso_id);
      if (!componentDidMount.current) return;
      if (res.is_success) {
          setDataSourceValidated(res.data);
      }
      setIsLoading(false);
    };

    const handleImport = async () => {
        setStepId(3); // Chuyển sang bước 3 (Import processing)
        setIsLoading(true);

        // Đơn giản: chỉ import với file gốc, backend sẽ tự bỏ qua dòng lỗi
        const datasend = {
            fileName: uploadedFileName,
            sheetIndex: 0
        };

        const res = await lichSuXuongPhongYTeApi.import(datasend, nam_hoc, dm_coso_id);
        if (!componentDidMount.current) return;

        if (res.is_success) {
            // Lưu kết quả import để hiển thị ở bước 4
            setImportResult({
                total: statistics.total,
                success: statistics.validCount, // Số dòng hợp lệ
                error: statistics.errorCount,
                data: dataSourceValidated
            });
            setStepId(4); // Chuyển sang bước 4 (Done)
            NotifyHelper.Success(`Import hoàn tất! ${statistics.validCount} dòng hợp lệ đã được import.`);
        } else {
            NotifyHelper.Error("Import thất bại: " + (res.message || "Lỗi không xác định"));
            setStepId(2); // Quay lại bước 2 nếu lỗi
        }
        setIsLoading(false);
    };

    const tableHeaders = useMemo(() => {
        if (!dataSourceValidated || dataSourceValidated.length === 0) return [];
        const keys = Object.keys(dataSourceValidated[0]).filter((key) => key !== "0" && key !== "ID");
        return ['Kết quả', ...keys];
    }, [dataSourceValidated]);

    // Download functions
    const downloadExcel = (data: any[], filename: string) => {
        if (!data || data.length === 0) return;

        // Prepare data for Excel
        const excelData = data.map(row => {
            const newRow: any = {};
            newRow['Kết quả'] = row.err ? row.err : 'Hợp lệ';

            // Add other columns
            tableHeaders.slice(1).forEach(header => {
                newRow[header] = row[header] || '';
            });

            return newRow;
        });

        // Create Excel workbook
        const worksheet = XLSX.utils.json_to_sheet(excelData);

        // Set column widths
        const columnWidths = tableHeaders.map(header => ({
            wch: Math.max(
                header.length,
                ...excelData.map(row => String(row[header] || '').length)
            ) + 2
        }));
        worksheet['!cols'] = columnWidths;

        // Create workbook and add worksheet
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Kết quả validate');

        // Generate Excel file and download
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
        });
        saveAs(blob, filename);
    };

    // Download functions for step 2 (validate)
    const handleDownloadAllValidate = async () => {
        downloadExcel(dataSourceValidated, 'ket_qua_validate_tat_ca.xlsx');
    };

    const handleDownloadSuccessValidate = () => {
        const successData = dataSourceValidated.filter((x: any) => !x.err || x.err.trim() === "");
        downloadExcel(successData, 'ket_qua_validate_thanh_cong.xlsx');
    };

    const handleDownloadErrorValidate = () => {
        const errorData = dataSourceValidated.filter((x: any) => x.err && x.err.trim() !== "");
        downloadExcel(errorData, 'ket_qua_validate_loi.xlsx');
    };

    // Download functions for step 4 (result)
    const handleDownloadAll = () => {

        downloadExcel(importResult?.data || [], 'ket_qua_import_tat_ca.xlsx');
    };

    const handleDownloadSuccess = () => {
        const successData = (importResult?.data || []).filter((x: any) => !x.err || x.err.trim() === "");
        downloadExcel(successData, 'ket_qua_import_thanh_cong.xlsx');
    };

    const handleDownloadError = () => {
        const errorData = (importResult?.data || []).filter((x: any) => x.err && x.err.trim() !== "");
        downloadExcel(errorData, 'ket_qua_import_loi.xlsx');
    };

    // Add styles to make modal full screen
    useEffect(() => {
        // Create style element for full screen modal
        const styleElement = document.createElement('style');
        styleElement.innerHTML = `
            #${modalId} > div {
                max-width: 100% !important;
                width: 100% !important;
                height: 100vh !important;
                margin: 0 !important;
                border-radius: 0 !important;
            }
            #${modalId} .popup-header {
                padding: 16px 24px;
            }
            #${modalId} {
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                z-index: 1000;
            }
            /* Custom table scrolling styles */
            .import-lich-su-y-te-container table {
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
            }

            /* Smooth scrollbars */
            .table-container::-webkit-scrollbar {
                height: 8px;
                width: 8px;
            }

            .table-container::-webkit-scrollbar-track {
                background: #f6f8fa;
                border-radius: 4px;
            }

            .table-container::-webkit-scrollbar-thumb {
                background: #d1d5da;
                border-radius: 4px;
            }

            .table-container::-webkit-scrollbar-thumb:hover {
                background: #c1c8cd;
            }

            /* Sticky columns for first 4 columns */
            .import-lich-su-y-te-container .sticky-col-0 {
                position: sticky;
                left: 0;
                background: #f6f8fa;
                z-index: 6;
                box-shadow: 2px 0 4px rgba(0,0,0,0.1);
                width: 152px;
                min-width: 152px;
                max-width: 152px;
            }

            .import-lich-su-y-te-container .sticky-col-1 {
                position: sticky;
                left: 150px; /* Width of first column - 2px overlap */
                background: #f6f8fa;
                z-index: 5;
                box-shadow: 2px 0 4px rgba(0,0,0,0.1);
                width: 152px;
                min-width: 152px;
                max-width: 152px;
            }

            .import-lich-su-y-te-container .sticky-col-2 {
                position: sticky;
                left: 300px; /* Width of first + second column - 2px overlap */
                background: #f6f8fa;
                z-index: 4;
                box-shadow: 2px 0 4px rgba(0,0,0,0.1);
                width: 152px;
                min-width: 152px;
                max-width: 152px;
            }

            .import-lich-su-y-te-container .sticky-col-3 {
                position: sticky;
                left: 450px; /* Width of first + second + third column - 2px overlap */
                background: #f6f8fa;
                z-index: 3;
                box-shadow: 2px 0 4px rgba(0,0,0,0.1);
                width: 152px;
                min-width: 152px;
                max-width: 152px;
            }

            /* All table cells have equal width and allow text wrapping */
            .import-lich-su-y-te-container table th,
            .import-lich-su-y-te-container table td {
                width: 150px;
                min-width: 150px;
                max-width: 150px;
                white-space: normal !important;
                word-wrap: break-word;
                word-break: break-word;
                overflow-wrap: break-word;
            }

            /* Body cells for sticky columns */
            .import-lich-su-y-te-container tbody .sticky-col-0 {
                background: #ffffff;
            }

            .import-lich-su-y-te-container tbody tr:nth-child(even) .sticky-col-0,
            .import-lich-su-y-te-container tbody tr:nth-child(even) .sticky-col-1,
            .import-lich-su-y-te-container tbody tr:nth-child(even) .sticky-col-2,
            .import-lich-su-y-te-container tbody tr:nth-child(even) .sticky-col-3 {
                background: #f6f8fa;
            }

            .import-lich-su-y-te-container tbody tr:nth-child(odd) .sticky-col-0,
            .import-lich-su-y-te-container tbody tr:nth-child(odd) .sticky-col-1,
            .import-lich-su-y-te-container tbody tr:nth-child(odd) .sticky-col-2,
            .import-lich-su-y-te-container tbody tr:nth-child(odd) .sticky-col-3 {
                background: #ffffff;
            }
        `;
        document.head.appendChild(styleElement);

        // Apply ID to the modal once it renders
        setTimeout(() => {
            const modalElement = document.querySelector('.animation-popup');
            if (modalElement) {
                modalElement.id = modalId;
            }
        }, 100);

        return () => {
            document.head.removeChild(styleElement);
        };
    }, [modalId]);

    return (
        <AnimationPopup
            animationOf={animationOf}
            width="100%"  // Set width to 100% - this prop seems to be accepted
            title='Import lịch sử xuống phòng y tế'
            onClose={onClose}
        >
            <Box style={{
                display: "flex",
                flexDirection: "column",
                height: "calc(100vh - 220px)",
                overflow: "hidden"
            }} className="import-lich-su-y-te-container">
                <Box style={{ marginBottom: "24px" }}>
                    <Steps steps={_steps.map(x => ({
                        ...x,
                        is_active: x.id === stepId
                    }))} />
                </Box>

                <Box style={{ display: "flex", marginBottom: "24px" }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', width: '100%' }}>
                        <Button
                            leadingVisual={UploadIcon}
                            text='Upload file'
                            onClick={handleSelectFile}
                            size='medium'
                            isLoading={isLoading}
                        />
                        {uploadedFileName && (
                            <TextInput
                                style={{ width: 400 }}
                                value={uploadedFileName}
                                readOnly
                            />
                        )}
                        <Box style={{ flex: 1 }} />
                        <Link href={`${appInfo.baseApiURL.replace('/api', '')}/TEMPLATE/IMPORT_LICHSU_YTE_TEMPLATE_NEW.csv`} target='_blank'>
                            <Button
                                text='Tải mẫu import lịch sử y tế tại đây'
                                variant='invisible'
                                leadingVisual={DownloadIcon}
                            />
                        </Link>
                    </div>
                </Box>

                {stepId === 2 && (
                    <Box style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "auto",
                        overflowX: "auto",
                        height: "calc(100vh - 220px)",
                        overflowY: "auto"
                    }}>
                        {/* Statistics and Filter Section */}
                        <Box style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '12px',
                            padding: '8px 12px',
                            backgroundColor: '#f6f8fa',
                            borderRadius: '4px',
                            border: '1px solid #e1e4e8'
                        }}>
                            <Box style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                                <span style={{ fontSize: '13px', fontWeight: '500' }}>
                                    Tổng: <span style={{ color: '#0969da', fontWeight: '600' }}>{statistics.total}</span>
                                </span>
                                <span style={{ fontSize: '13px', fontWeight: '500' }}>
                                    OK: <span style={{ color: '#1a7f37', fontWeight: '600' }}>{statistics.validCount}</span>
                                </span>
                                <span style={{ fontSize: '13px', fontWeight: '500' }}>
                                    Lỗi: <span style={{ color: '#d1242f', fontWeight: '600' }}>{statistics.errorCount}</span>
                                </span>
                                {filterStatus !== 'all' && (
                                    <span style={{ fontSize: '13px', fontWeight: '500' }}>
                                        Hiển thị: <span style={{ color: '#8250df', fontWeight: '600' }}>{filteredData.length}</span>
                                    </span>
                                )}
                            </Box>
                            <Box style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span style={{ fontSize: '13px', fontWeight: '500' }}>Lọc:</span>
                                <Select
                                    value={filterStatus}
                                    onChange={(e) => setFilterStatus(e.target.value)}
                                    size="small"
                                    style={{ minWidth: '100px', fontSize: '13px' }}
                                >
                                    <Select.Option value="all">Tất cả</Select.Option>
                                    <Select.Option value="valid">Hợp lệ</Select.Option>
                                    <Select.Option value="error">Lỗi</Select.Option>
                                </Select>
                            </Box>
                        </Box>

                        {/* Download Buttons Section */}
                        <Box style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            marginBottom: "12px",
                            padding: "8px 12px",
                            backgroundColor: "#ffffff",
                            borderRadius: "4px",
                            border: "1px solid #e1e4e8"
                        }}>
                            <span style={{ fontSize: "13px", fontWeight: "500", color: "#24292f" }}>Tải xuống:</span>
                            <Button
                                leadingVisual={DownloadIcon}
                                text={`Tất cả (${statistics.total})`}
                                onClick={handleDownloadAllValidate}
                                variant="default"
                                size="small"
                            />
                            <Button
                                leadingVisual={DownloadIcon}
                                text={`Hợp lệ (${statistics.validCount})`}
                                onClick={handleDownloadSuccessValidate}
                                variant="default"
                                size="small"
                                disabled={statistics.validCount === 0}
                            />
                            <Button
                                leadingVisual={DownloadIcon}
                                text={`Lỗi (${statistics.errorCount})`}
                                onClick={handleDownloadErrorValidate}
                                variant="default"
                                size="small"
                                disabled={statistics.errorCount === 0}
                            />
                        </Box>
                        {isLoading ? (
                            <Box style={{ textAlign: 'center', padding: '20px' }}>
                                Đang tải...
                            </Box>
                        ) : (
                            <div className="table-container" style={{
                                overflowX: 'auto',
                                overflowY: 'auto',
                                border: '1px solid #e1e4e8',
                                borderRadius: '6px'
                            }}>
                                <table style={{
                                    width: '100%',
                                    borderCollapse: 'collapse',
                                    minWidth: 'max-content'
                                }}>
                                    <thead style={{
                                        backgroundColor: '#f6f8fa',
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 1
                                    }}>
                                        <tr>
                                            {tableHeaders.map((header, index) => (
                                                <th key={index}
                                                    className={index < 4 ? `sticky-col-${index}` : ''}
                                                    style={{
                                                    padding: '12px 16px',
                                                    textAlign: 'left',
                                                    borderBottom: '1px solid #e1e4e8',
                                                    borderRight: index < tableHeaders.length - 1 ? '1px solid #e1e4e8' : 'none',
                                                    fontWeight: '600',
                                                    fontSize: '14px'
                                                }}>
                                                    {header}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredData.map((row, rowIndex) => (
                                            <tr key={rowIndex} style={{
                                                backgroundColor: rowIndex % 2 === 0 ? '#ffffff' : '#f6f8fa'
                                            }}>
                                                {/* Cột kết quả */}
                                                <td className="sticky-col-0" style={{
                                                    padding: '12px 16px',
                                                    borderBottom: '1px solid #e1e4e8',
                                                    borderRight: '1px solid #e1e4e8',
                                                    verticalAlign: 'top'
                                                }}>
                                                    {row.err ? (
                                                        <FormControl.Validation variant='error'>
                                                            <div style={{
                                                                whiteSpace: 'pre-line',
                                                                fontSize: '12px',
                                                                lineHeight: '1.4'
                                                            }}>
                                                                {row.err}
                                                            </div>
                                                        </FormControl.Validation>
                                                    ) : (
                                                        <FormControl.Validation variant='success'>
                                                            ✓ Hợp lệ
                                                        </FormControl.Validation>
                                                    )}
                                                </td>

                                                {/* Các cột dữ liệu */}
                                                {tableHeaders.slice(1).map((header, cellIndex) => (
                                                    <td key={cellIndex}
                                                        className={cellIndex < 3 ? `sticky-col-${cellIndex + 1}` : ''}
                                                        style={{
                                                        padding: '12px 16px',
                                                        borderBottom: '1px solid #e1e4e8',
                                                        borderRight: cellIndex < tableHeaders.length - 2 ? '1px solid #e1e4e8' : 'none',
                                                        fontSize: '14px'
                                                    }}>
                                                        {row[header] || ''}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Box>
                )}

                {stepId === 3 && (
                    <Box style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        textAlign: "center"
                    }}>
                        <div style={{
                            width: "60px",
                            height: "60px",
                            border: "4px solid #f3f3f3",
                            borderTop: "4px solid #0969da",
                            borderRadius: "50%",
                            animation: "spin 1s linear infinite",
                            marginBottom: "24px"
                        }}></div>
                        <h3 style={{ margin: "0 0 12px 0", color: "#24292f" }}>Đang import dữ liệu...</h3>
                        <p style={{ margin: 0, color: "#656d76" }}>Vui lòng đợi trong giây lát</p>
                        <style>{`
                            @keyframes spin {
                                0% { transform: rotate(0deg); }
                                100% { transform: rotate(360deg); }
                            }
                        `}</style>
                    </Box>
                )}

                {stepId === 4 && importResult && (
                    <Box style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "auto",
                        padding: "12px"
                    }}>
                        {/* Header và Statistics trong một hàng */}
                        <Box style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: "16px",
                            padding: "12px 16px",
                            backgroundColor: "#f6f8fa",
                            borderRadius: "6px",
                            border: "1px solid #e1e4e8"
                        }}>
                            {/* Success Icon và Text */}
                            <Box style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                <div style={{
                                    width: "40px",
                                    height: "40px",
                                    backgroundColor: "#1a7f37",
                                    borderRadius: "50%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center"
                                }}>
                                    <CheckIcon size={24} fill="white" />
                                </div>
                                <Box>
                                    <h4 style={{ margin: "0", color: "#1a7f37", fontSize: "16px" }}>Import hoàn tất!</h4>
                                    <p style={{ margin: "0", color: "#656d76", fontSize: "12px" }}>
                                        Dữ liệu đã được import thành công
                                    </p>
                                </Box>
                            </Box>

                            {/* Statistics */}
                            <Box style={{ display: "flex", gap: "24px", alignItems: "center" }}>
                                <Box style={{ textAlign: "center" }}>
                                    <div style={{ fontSize: "20px", fontWeight: "bold", color: "#0969da" }}>
                                        {importResult.total}
                                    </div>
                                    <div style={{ fontSize: "11px", color: "#656d76" }}>Tổng số</div>
                                </Box>
                                <Box style={{ textAlign: "center" }}>
                                    <div style={{ fontSize: "20px", fontWeight: "bold", color: "#1a7f37" }}>
                                        {importResult.success}
                                    </div>
                                    <div style={{ fontSize: "11px", color: "#656d76" }}>Thành công</div>
                                </Box>
                                <Box style={{ textAlign: "center" }}>
                                    <div style={{ fontSize: "20px", fontWeight: "bold", color: "#d1242f" }}>
                                        {importResult.error}
                                    </div>
                                    <div style={{ fontSize: "11px", color: "#656d76" }}>Lỗi</div>
                                </Box>
                            </Box>
                        </Box>

                        {/* Download Buttons - Inline */}
                        <Box style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            marginBottom: "16px",
                            padding: "8px 12px",
                            backgroundColor: "#ffffff",
                            borderRadius: "4px",
                            border: "1px solid #e1e4e8"
                        }}>
                            <span style={{ fontSize: "14px", fontWeight: "500", color: "#24292f" }}>Tải xuống:</span>
                            <Button
                                leadingVisual={DownloadIcon}
                                text={`Tất cả (${importResult.total})`}
                                onClick={handleDownloadAll}
                                variant="default"
                                size="small"
                            />
                            <Button
                                leadingVisual={DownloadIcon}
                                text={`Thành công (${importResult.success})`}
                                onClick={handleDownloadSuccess}
                                variant="default"
                                size="small"
                                disabled={importResult.success === 0}
                            />
                            <Button
                                leadingVisual={DownloadIcon}
                                text={`Lỗi (${importResult.error})`}
                                onClick={handleDownloadError}
                                variant="default"
                                size="small"
                                disabled={importResult.error === 0}
                            />
                        </Box>

                        {/* Data Table - Compact */}
                        <Box style={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            minHeight: "250px"
                        }}>
                            {/* Filter Section - Inline */}
                            <Box style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '8px',
                                padding: '4px 8px',
                                backgroundColor: '#f6f8fa',
                                borderRadius: '3px',
                                border: '1px solid #e1e4e8'
                            }}>
                                <Box style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                    <span style={{ fontSize: '11px', fontWeight: '500' }}>
                                        Tổng: <span style={{ color: '#0969da', fontWeight: '600' }}>{statistics.total}</span>
                                    </span>
                                    <span style={{ fontSize: '11px', fontWeight: '500' }}>
                                        OK: <span style={{ color: '#1a7f37', fontWeight: '600' }}>{statistics.validCount}</span>
                                    </span>
                                    <span style={{ fontSize: '11px', fontWeight: '500' }}>
                                        Lỗi: <span style={{ color: '#d1242f', fontWeight: '600' }}>{statistics.errorCount}</span>
                                    </span>
                                    {filterStatus !== 'all' && (
                                        <span style={{ fontSize: '11px', fontWeight: '500' }}>
                                            Hiển thị: <span style={{ color: '#8250df', fontWeight: '600' }}>{filteredData.length}</span>
                                        </span>
                                    )}
                                </Box>
                                <Box style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <span style={{ fontSize: '11px', fontWeight: '500' }}>Lọc:</span>
                                    <Select
                                        value={filterStatus}
                                        onChange={(e) => setFilterStatus(e.target.value)}
                                        size="small"
                                        style={{ minWidth: '80px', fontSize: '11px' }}
                                    >
                                        <Select.Option value="all">Tất cả</Select.Option>
                                        <Select.Option value="valid">Hợp lệ</Select.Option>
                                        <Select.Option value="error">Lỗi</Select.Option>
                                    </Select>
                                </Box>
                            </Box>

                            {/* Table Container */}
                            <div className="table-container" style={{
                                flex: 1,
                                overflow: "auto",
                                border: "1px solid #e1e4e8",
                                borderRadius: "4px",
                                backgroundColor: "#ffffff",
                            }}>
                                <table style={{
                                    width: "100%",
                                    borderCollapse: "collapse",
                                    tableLayout: "fixed"
                                }}>
                                    <thead style={{
                                        position: "sticky",
                                        top: 0,
                                        backgroundColor: "#f6f8fa",
                                        zIndex: 2
                                    }}>
                                        <tr>
                                            {tableHeaders.map((header, index) => (
                                                <th key={index}
                                                    className={index < 4 ? `sticky-col-${index}` : ''}
                                                    style={{
                                                    padding: '8px 12px',
                                                    textAlign: 'left',
                                                    borderBottom: '1px solid #e1e4e8',
                                                    borderRight: index < tableHeaders.length - 1 ? '1px solid #e1e4e8' : 'none',
                                                    fontWeight: '600',
                                                    fontSize: '12px'
                                                }}>
                                                    {header}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredData.map((row, rowIndex) => (
                                            <tr key={rowIndex} style={{
                                                backgroundColor: rowIndex % 2 === 0 ? "#ffffff" : "#f6f8fa"
                                            }}>
                                                {/* Cột kết quả */}
                                                <td className="sticky-col-0" style={{
                                                    padding: '8px 12px',
                                                    borderBottom: '1px solid #e1e4e8',
                                                    borderRight: '1px solid #e1e4e8',
                                                    verticalAlign: 'top'
                                                }}>
                                                    {row.err ? (
                                                        <FormControl.Validation variant='error'>
                                                            <div style={{
                                                                whiteSpace: 'pre-line',
                                                                fontSize: '11px',
                                                                lineHeight: '1.3'
                                                            }}>
                                                                {row.err}
                                                            </div>
                                                        </FormControl.Validation>
                                                    ) : (
                                                        <FormControl.Validation variant='success'>
                                                            <span style={{ fontSize: '11px' }}>✓ Hợp lệ</span>
                                                        </FormControl.Validation>
                                                    )}
                                                </td>

                                                {/* Các cột dữ liệu */}
                                                {tableHeaders.slice(1).map((header, cellIndex) => (
                                                    <td key={cellIndex}
                                                        className={cellIndex < 3 ? `sticky-col-${cellIndex + 1}` : ''}
                                                        style={{
                                                        padding: '8px 12px',
                                                        borderBottom: '1px solid #e1e4e8',
                                                        borderRight: cellIndex < tableHeaders.length - 2 ? '1px solid #e1e4e8' : 'none',
                                                        fontSize: '12px'
                                                    }}>
                                                        {row[header] || ''}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </Box>
                    </Box>
                )}

                <div style={{
                    position: 'sticky',
                    bottom: 0,
                    backgroundColor: 'white',
                    padding: '16px 0',
                    borderTop: '1px solid #e1e4e8'
                }}>
                    <PopUpFormActions>
                        <Button
                            leadingVisual={XIcon}
                            text='Base.Label.Close'
                            onClick={onClose}
                        />
                        {stepId === 2 && (
                            <Button
                                leadingVisual={CheckIcon}
                                variant='primary'
                                isLoading={isLoading}
                                text={'ThoiKhoaBieuImport.Label.Save'}
                                onClick={handleImport}
                            />
                        )}
                        {stepId === 4 && (
                            <Button
                                leadingVisual={CheckIcon}
                                variant='primary'
                                text='Hoàn tất'
                                onClick={onClose}
                            />
                        )}
                    </PopUpFormActions>
                </div>
            </Box>
        </AnimationPopup>
    );
};

export default ImportLichSuYTeModal;
